// Short guided first drive (DESIGN.md §5): a few on-track prompts that each
// complete when the player has done the thing, then straight into a lap.

import { addTapHaptic } from '../input/haptics';

const DONE_KEY = 'fone.onboarded';

interface Step {
  text: string;
  /** Returns true when the step is complete. */
  done(s: OnboardingInput, elapsed: number): boolean;
}

export interface OnboardingInput {
  steer: number;
  throttle: number;
  brake: number;
  recentred: boolean;
}

export function hasOnboarded(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return false;
  }
}

function markOnboarded(): void {
  try {
    localStorage.setItem(DONE_KEY, '1');
  } catch {
    // Will show again next time; harmless.
  }
}

export function resetOnboarding(): void {
  try {
    localStorage.removeItem(DONE_KEY);
  } catch {
    // Nothing to reset.
  }
}

export class Onboarding {
  private readonly el: HTMLElement;
  private readonly textEl: HTMLElement;
  private index = 0;
  private elapsed = 0;
  private steeredLeft = false;
  private steeredRight = false;
  private held = 0;
  active = false;

  private readonly steps: Step[] = [
    {
      text: 'Tilt your phone like a steering wheel. Try steering <b>left</b> and <b>right</b>.',
      done: (s) => {
        if (s.steer < -0.4) this.steeredLeft = true;
        if (s.steer > 0.4) this.steeredRight = true;
        return this.steeredLeft && this.steeredRight;
      },
    },
    { text: 'Hold the <b>right</b> side of the screen to accelerate.', done: (s) => this.hold(s.throttle > 0, 1.0) },
    { text: 'Hold the <b>left</b> side of the screen to brake.', done: (s) => this.hold(s.brake > 0, 0.6) },
    {
      text: 'If the car pulls when you hold the phone straight, tap the <b>speedometer</b> to re-centre.',
      done: (s, t) => s.recentred || t > 7,
    },
    {
      text: 'You’re set. The lap starts at the line, and the <b>300 / 200 / 100</b> boards count down to the big braking zones.',
      done: (_s, t) => t > 5,
    },
  ];

  private dt = 0;

  constructor(root: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'coach';
    this.el.innerHTML = `<div class="coach-step"></div><div class="coach-text"></div><button class="coach-skip">Skip</button>`;
    root.appendChild(this.el);
    this.textEl = this.el.querySelector('.coach-text') as HTMLElement;
    const skip = this.el.querySelector('.coach-skip') as HTMLElement;
    skip.addEventListener('click', () => this.finish());
    skip.addEventListener('pointerdown', (e) => e.stopPropagation());
    addTapHaptic(skip);
  }

  private hold(pressed: boolean, seconds: number): boolean {
    this.held = pressed ? this.held + this.dt : 0;
    return this.held >= seconds;
  }

  start(): void {
    this.active = true;
    this.index = 0;
    this.steeredLeft = this.steeredRight = false;
    this.show();
  }

  private show(): void {
    const step = this.steps[this.index]!;
    this.elapsed = 0;
    this.held = 0;
    (this.el.querySelector('.coach-step') as HTMLElement).textContent = `${this.index + 1} / ${this.steps.length}`;
    this.textEl.innerHTML = step.text;
    this.el.classList.add('show');
  }

  update(input: OnboardingInput, dt: number): void {
    if (!this.active) return;
    this.dt = dt;
    this.elapsed += dt;
    if (this.steps[this.index]!.done(input, this.elapsed)) {
      this.index++;
      if (this.index >= this.steps.length) this.finish();
      else this.show();
    }
  }

  /** Hide without completing (e.g. starting a race). */
  stop(): void {
    this.active = false;
    this.el.classList.remove('show');
  }

  private finish(): void {
    this.active = false;
    this.el.classList.remove('show');
    markOnboarded();
  }
}
